import type { CSSProperties } from 'react';

interface Props {
  size?: number;
  variant?: 'gradient' | 'mono' | 'white';
  spin?: boolean;
  className?: string;
  style?: CSSProperties;
}

const PETALS = [
  '#EE263E', '#DE7C00', '#FFB92A', '#FDDB32',
  '#98D801', '#31B700', '#00C072', '#00BBDE',
  '#0090E2', '#835DD0', '#8345BA', '#C04CAE'
] as const;

/**
 * Símbolo Bow — anel de pétalas arco-íris sobre o "horizonte" navy.
 * 12 pétalas distribuídas em 180° na metade superior; semicírculo
 * inferior preenche o navy.
 *
 * `variant`:
 *  - gradient: pétalas arco-íris + horizonte navy (default — uso colorido).
 *  - mono:     tudo navy (uso em fundos cream / contextos monocromáticos).
 *  - white:    tudo cream (uso em fundos navy).
 *
 * `spin`: roda 360° em loop infinito (uso decorativo, ex.: avatar do perfil).
 */
export function BowArc({ size = 64, variant = 'gradient', spin = false, className = '', style }: Props) {
  const cx = 50;
  const cy = 50;
  const rOuter = 46;
  const rInner = 28;

  const segments = [];
  for (let i = 0; i < 12; i++) {
    const a0 = Math.PI - (i * Math.PI) / 12;
    const a1 = Math.PI - ((i + 1) * Math.PI) / 12;
    const x0o = cx + rOuter * Math.cos(a0);
    const y0o = cy - rOuter * Math.sin(a0);
    const x1o = cx + rOuter * Math.cos(a1);
    const y1o = cy - rOuter * Math.sin(a1);
    const x0i = cx + rInner * Math.cos(a0);
    const y0i = cy - rInner * Math.sin(a0);
    const x1i = cx + rInner * Math.cos(a1);
    const y1i = cy - rInner * Math.sin(a1);
    const path = `M ${x0o} ${y0o} A ${rOuter} ${rOuter} 0 0 1 ${x1o} ${y1o} L ${x1i} ${y1i} A ${rInner} ${rInner} 0 0 0 ${x0i} ${y0i} Z`;
    const fill =
      variant === 'mono' ? '#001B74' : variant === 'white' ? '#F1EDE4' : PETALS[i];
    segments.push(<path key={i} d={path} fill={fill} />);
  }
  const groundFill = variant === 'white' ? '#F1EDE4' : '#001B74';
  const ground = `M ${cx - rOuter} ${cy} A ${rOuter} ${rOuter} 0 0 0 ${cx + rOuter} ${cy} L ${cx + rInner} ${cy} A ${rInner} ${rInner} 0 0 1 ${cx - rInner} ${cy} Z`;

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      style={{
        ...style,
        animation: spin ? 'bow-spin 18s linear infinite' : undefined
      }}
      aria-hidden
    >
      {segments}
      <path d={ground} fill={groundFill} />
    </svg>
  );
}
